import notifyHB from '@/config/notifyHB';
import { PDFDocument, PDFFont } from 'pdf-lib';
import { useEffect, useMemo, useState } from 'react';
import { getFont } from '../utils/pdf';

export function usePdfTextMetrics() {
	const [font, setFont] = useState<PDFFont | null>(null);

	useEffect(() => {
		let isCancelled = false;
		const loadAndSetFont = async () => {
			try {
				const pdf = await PDFDocument.create();
				const font = await getFont(pdf);
				if (!isCancelled) setFont(font);
			} catch (exception) {
				console.log(exception);
			}
		};
		loadAndSetFont().catch((error) =>
			notifyHB(
				'Util',
				'use-pdf-text-metrics',
				`Failed to set font with ${error}`,
			),
		);
		return () => {
			isCancelled = true;
		};
	}, []);

	const api = useMemo(() => {
		if (!font) return null;

		return {
			/**
			 * Measure a single-line string in PDF points and pixels.
			 * @param text           e.g. "2025-09-17"
			 * @param fontSizePt     e.g. 12 (points)
			 * @param pageScale      e.g. whatever you passed into <Page scale={...} />
			 */
			measure(
				text: string,
				fontSizePt: number = 14,
				pageScale: number = 1,
			) {
				const widthPt = font.widthOfTextAtSize(
					text.replace(' ', '_'),
					fontSizePt,
				);
				const heightPt = font.heightAtSize(fontSizePt);
				return {
					widthPt,
					heightPt,
					widthPx: widthPt * pageScale + 12, //Include horizontal padding
					heightPx: heightPt * pageScale,
				};
			},

			/**
			 * Given a target on-screen width in pixels, solve for the font size (pt)
			 * that will print to that width when burned into the PDF.
			 */
			fontSizeForTargetPixelWidth(
				text: string,
				targetWidthPx: number,
				pageScale: number,
			) {
				const widthAt1Pt = font.widthOfTextAtSize(text, 1);
				// widthPx = (widthAt1Pt * fontSizePt) * pageScale
				// => fontSizePt = targetWidthPx / (widthAt1Pt * pageScale)
				return targetWidthPx / (widthAt1Pt * pageScale);
			},

			font, // expose it if you also want to reuse the same PDFFont when printing
		};
	}, [font]);

	return api;
}
